import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Button,
  Space,
  Modal,
  Upload,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { BackDetails } from '../../../../types/BackDetails';

interface BackDetailsModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: () => void;
  initialValues?: BackDetails | null;
  isEditMode?: boolean;
  backDetails: BackDetails[];
}

const BackDetailsModal: React.FC<BackDetailsModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
  backDetails,
}) => {
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([]);


  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');


  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  };

  // image preview 
  const handlePreview = async (file: UploadFile) => {
      if (!file.url && !file.preview) {
          file.preview = await getBase64(file.originFileObj as File);
      }

      setPreviewImage(file.url || (file.preview as string));
      setPreviewOpen(true);
      setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  // Utility function to convert image to base64
  const getBase64 = (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = error => reject(error);
      });


  const handleSubmit = async (values: any) => {
    try {
      const formData = new FormData();
      formData.append('bd_name', values.bd_name);

      if (imageFile) {
        formData.append('attachments', imageFile);
      }

      if (initialValues?.bd_id) {
        formData.append('bd_id', initialValues.bd_id.toString());
      }

      const url = isEditMode ? 'edit-back-detail' : 'add-back-detail';
      const method = isEditMode ? 'put' : 'post';

      await apiRequest({
        method,
        url,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onCreate();
      showSuccess(isEditMode ? 'Back Detail updated successfully!' : 'Back Detail created successfully!');
      form.resetFields();
      if (!isModalClose) {
            setImageFile(null);
            setImageFileList([]);
        }

      if (isModalClose) {
        onCancel();
      }
    } catch {
      showError(`Failed to ${isEditMode ? 'update' : 'create'} back detail`);
    }
  };

  useEffect(() => {
    if (visible && isEditMode && initialValues) {
      form.setFieldsValue({
        bd_name: initialValues.bd_name,
      });
      if (initialValues.bd_image) {
        setImageFileList([
          {
            uid: '-1',
            name: 'image.png',
            status: 'done',
            url: initialValues.bd_image,
          },
        ]);
        setImageFile(null);
      }
    } else if (visible) {
      form.resetFields();
      setImageFileList([]);
      setImageFile(null);
    }
  }, [visible, isEditMode, initialValues, form]);

  const uploadButton = (
    <div
      style={{
        width: 220,
        height: 220,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <PlusOutlined style={{ fontSize: 24 }} />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
      <>
        <Modal
          title={isEditMode ? 'Edit Back Detail' : 'Add New Back Detail'}
          visible={visible}
          onCancel={onCancel}
          footer={null}
          width="90%"
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="bd_name"
                  className="px-8"
                  label={
                    <span>
                      Back Detail Name&nbsp;
                      <span style={{ fontSize: '12px', color: '#888' }}>
                        (Max 100 characters)
                      </span>
                    </span>
                  }
                  rules={[
                    { required: true, message: 'Please enter the back detail name' },
                    { max: 100, message: 'Maximum 100 characters allowed' },
                  ]}
                >
                  <Input
                    showCount
                    maxLength={100}
                    placeholder="e.g., Center Back Seam"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Upload Back Detail Image" required>
                  <Upload
                    beforeUpload={() => false}
                    listType="picture-card"
                    maxCount={1}
                    fileList={imageFileList}
                    onPreview={handlePreview} // 👈 Add this
                    onChange={({ fileList }) => {
                      setImageFileList(fileList);
                      const rawFile = fileList[0]?.originFileObj;
                      if (rawFile instanceof File) {
                        setImageFile(rawFile);
                      } else {
                        setImageFile(null);
                      }
                    }}
                  >
                    {imageFileList.length >= 1 ? null : uploadButton}
                  </Upload>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Space>
                <Button onClick={onCancel}>Cancel</Button>
                {!isEditMode && (
                  <Button
                    type="primary"
                    htmlType="submit"
                    onClick={() => setIsModalClose(false)}
                    disabled={isSubmitDisabled()}
                  >
                    Submit & Create New Back Detail
                  </Button>
                )}
                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={() => setIsModalClose(true)}
                  disabled={isSubmitDisabled()}
                >
                  Submit
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
        <Modal
            visible={previewOpen}
            title={previewTitle}
            footer={null}
            centered
            onCancel={() => setPreviewOpen(false)}
        >
            <img
                alt="Preview"
                style={{ width: '100%' }}
                src={previewImage}
        />
        </Modal>
      </>
  );
};

export default BackDetailsModal;
